/** A numeric time-series point with millisecond timestamp. */
export interface TimeValuePoint {
  timestamp: number;
  value: number;
}

/**
 * Centered simple moving average over a time window.
 *
 * For each input point at time t, averages every point whose timestamp falls
 * within [t - windowMs/2, t + windowMs/2] (inclusive on both bounds). Output is
 * sampled at the original timestamps so it aligns with the raw series on the chart.
 *
 * Runs in O(n) via a two-pointer sliding window. Input is sorted ascending by
 * timestamp first (defensive — callers may pass unsorted data).
 *
 * NOTE: this is an UNWEIGHTED average over the points that fall in the window,
 * not a time-weighted integral. Within a single series, a dense burst of
 * readings (e.g. a device reconnecting and back-filling) is weighted by point
 * count, so the curve is pulled toward the burst. That is an accepted v1
 * tradeoff (centered SMA was chosen over time-weighted); a time-weighted
 * variant is deferred (see "Out of scope").
 */
export function computeCenteredMovingAverage(
  points: TimeValuePoint[],
  windowMs: number
): TimeValuePoint[] {
  if (points.length === 0) return [];

  const sorted = [...points].sort((a, b) => a.timestamp - b.timestamp);
  const half = windowMs / 2;

  const result: TimeValuePoint[] = new Array(sorted.length);
  let left = 0;
  let right = 0;
  let sum = 0;

  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i]!;
    const lo = cur.timestamp - half;
    const hi = cur.timestamp + half;

    // Expand right edge to include all points <= hi.
    while (right < sorted.length && sorted[right]!.timestamp <= hi) {
      sum += sorted[right]!.value;
      right++;
    }
    // Shrink left edge to drop points < lo.
    while (left < right && sorted[left]!.timestamp < lo) {
      sum -= sorted[left]!.value;
      left++;
    }

    const count = right - left;
    result[i] = {
      timestamp: cur.timestamp,
      value: count > 0 ? sum / count : cur.value,
    };
  }

  return result;
}
